/**
 * Weight display and plate math.
 *
 * Storage is always kilograms; `unit_pref` is presentation only. Every
 * conversion in the app goes through here so there is exactly one place for the
 * rounding rules to live — the classic version of this bug is a weight that
 * drifts by 0.1 kg every time it's edited, because it round-trips through
 * pounds with a different precision each way.
 */

export type Unit = 'kg' | 'lb'

const LB_PER_KG = 2.2046226218

export function kgToDisplay(kg: number | null | undefined, unit: Unit): number | null {
  if (kg === null || kg === undefined) return null
  return unit === 'kg' ? round(kg, 2) : round(kg * LB_PER_KG, 1)
}

export function displayToKg(value: number | null | undefined, unit: Unit): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null
  return unit === 'kg' ? round(value, 3) : round(value / LB_PER_KG, 3)
}

export function formatWeight(
  kg: number | null | undefined,
  unit: Unit,
  opts: { withUnit?: boolean } = {},
): string {
  const v = kgToDisplay(kg, unit)
  if (v === null) return '—'
  // Trim a trailing .0 — "100 lb" reads better than "100.0 lb", and lifters
  // don't think in tenths.
  const text = Number.isInteger(v) ? String(v) : String(v)
  return opts.withUnit === false ? text : `${text} ${unit}`
}

// --- time and distance ------------------------------------------------------
//
// Same posture as weight: storage is SI (seconds, metres) and the unit
// preference is presentation only. Someone who thinks in pounds thinks in
// miles, so distance rides `unit_pref` rather than earning a setting nobody
// wants to answer.

const M_PER_MILE = 1609.344

/**
 * Distance units, and who decides which one you get.
 *
 * Distance is NOT inferred from `unit_pref`. Inferring it is what caused the
 * bug this all exists to settle: pounds meant miles for every distance in the
 * app, so an eighteen-metre farmer's carry displayed as "0.01 mi" and had to be
 * typed as 0.0112 into a box labelled "Distance (mi)". In practice you type 18,
 * store eighteen MILES, and collect a distance record of twenty-nine
 * kilometres. It never errors — it produces a plausible number.
 *
 * Three inputs decide the unit, most specific first:
 *
 *   1. A per-exercise override, set by the toggle on the log form. Sprints in
 *      yards and jumps in feet is a completely reasonable thing to want, and no
 *      single global answer can express it.
 *   2. The account's `distance_pref` — metric, imperial, or auto.
 *   3. `auto`, which means metres for short work and miles/km for long work
 *      following unit_pref. That is what everybody had before the setting
 *      existed, so nobody's numbers move unless they ask.
 *
 * Everything is stored in METRES regardless, exactly as weight is always stored
 * in kilograms. The unit is a display and parsing concern and nothing else.
 */

export type DistanceScale = 'short' | 'long'
export type DistanceUnit = 'm' | 'yd' | 'ft' | 'km' | 'mi'
export type DistancePref = 'auto' | 'metric' | 'imperial'

/** Metres per unit. The only conversion table for distance in the app. */
const METRES_PER: Record<DistanceUnit, number> = {
  m: 1,
  yd: 0.9144,
  ft: 0.3048,
  km: 1000,
  mi: M_PER_MILE,
}

/**
 * What the toggle offers, in the order it shows them.
 *
 * Feet is offered for short work and not for long, because a broad jump is
 * written in feet and a 5 km run in feet is 16,404 of them.
 */
export const DISTANCE_UNITS: Record<DistanceScale, DistanceUnit[]> = {
  short: ['m', 'yd', 'ft'],
  long: ['km', 'mi'],
}

/** The unit an exercise gets when nobody has overridden it. */
export function defaultDistanceUnit(
  scale: DistanceScale,
  pref: DistancePref,
  unit: Unit,
): DistanceUnit {
  if (pref === 'metric') return scale === 'short' ? 'm' : 'km'
  if (pref === 'imperial') return scale === 'short' ? 'yd' : 'mi'
  // auto: unchanged from before the preference existed.
  if (scale === 'short') return 'm'
  return unit === 'lb' ? 'mi' : 'km'
}

/** The unit actually in force, honouring a per-exercise override. */
export function resolveDistanceUnit(opts: {
  scale: DistanceScale
  pref: DistancePref
  unit: Unit
  override?: DistanceUnit | null
}): DistanceUnit {
  const { scale, pref, unit, override } = opts
  // An override from a different scale is ignored rather than obeyed — an
  // exercise reclassified from long to short must not keep rendering in miles.
  if (override && DISTANCE_UNITS[scale].includes(override)) return override
  return defaultDistanceUnit(scale, pref, unit)
}

/** "45s", "1:30", "1:05:00" — the shortest form that stays unambiguous. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—'
  const total = Math.max(0, Math.round(seconds))
  if (total < 60) return `${total}s`

  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m)
  return h > 0
    ? `${h}:${mm}:${String(s).padStart(2, '0')}`
    : `${mm}:${String(s).padStart(2, '0')}`
}

export function formatDistance(
  metres: number | null | undefined,
  du: DistanceUnit,
  opts: { withUnit?: boolean } = {},
): string {
  if (metres === null || metres === undefined || Number.isNaN(metres)) return '—'
  const withUnit = opts.withUnit !== false
  const value = metres / METRES_PER[du]
  // Two decimals under ten, none above. A broad jump is 2.44 m and rounding it
  // to 2 throws away the only number the exercise produces; a 5000 m row does
  // not need to claim millimetres.
  const places = Math.abs(value) < 10 ? 2 : 0
  return `${round(value, places)}${withUnit ? ` ${du}` : ''}`
}

/**
 * How far one pace is quoted over, per distance unit.
 *
 * Pace used to key off the WEIGHT preference, so every distance in the app was
 * quoted per mile for a lb account. That was right for a run and wrong for
 * everything counted in metres — a 2000 m row read "6:26 /mi", a split no
 * rower has ever said, sitting one dot away from a distance that had just been
 * fixed to read 2000 m.
 *
 * Keying off the distance unit instead means the pace and the distance beside
 * it always agree, and it needs no new data: every caller already resolves the
 * unit for its exercise in order to render the distance at all.
 *
 * 500 m rather than a bare metre: it is the split every Concept2 quotes and
 * every erg programme is written in, "/m" would be a two-second number, and
 * "/km" would double the only figure a 2 k has. Yards and feet only arise if
 * somebody overrides an erg onto them, and they follow the same shape.
 */
const PACE_PER: Record<DistanceUnit, { metres: number; label: string }> = {
  mi: { metres: M_PER_MILE, label: '/mi' },
  km: { metres: 1000, label: '/km' },
  m: { metres: 500, label: '/500m' },
  yd: { metres: 500 * 0.9144, label: '/500yd' },
  ft: { metres: 500 * 0.3048, label: '/500ft' },
}

/**
 * Pace, the human-facing inverse of the stored speed. "5:20 /km", "1:58 /500m".
 *
 * Speed is what's stored and compared — see the note on SetLog.speed_ms — and
 * this is the only place the inversion happens.
 */
export function formatPace(
  metres: number | null | undefined,
  seconds: number | null | undefined,
  du: DistanceUnit,
): string {
  if (!metres || !seconds) return '—'
  const basis = PACE_PER[du] ?? PACE_PER.km
  const secondsPer = (seconds / metres) * basis.metres
  if (!Number.isFinite(secondsPer)) return '—'
  const m = Math.floor(secondsPer / 60)
  const s = Math.round(secondsPer % 60)
  return `${m}:${String(s).padStart(2, '0')} ${basis.label}`
}

/** Parse "90", "1:30" or "1:05:00" to seconds. Null if it isn't one of those. */
export function parseDuration(text: string): number | null {
  const raw = text.trim()
  if (!raw) return null
  const parts = raw.split(':')
  if (parts.some((p) => p !== '' && !/^\d+$/.test(p))) return null

  const nums = parts.map((p) => (p === '' ? 0 : parseInt(p, 10)))
  if (nums.some(Number.isNaN)) return null

  // A bare number is seconds: mid-workout, "45" for a plank means 45 seconds,
  // and reading it as 45 minutes would be absurd rather than merely wrong.
  if (nums.length === 1) return nums[0]
  if (nums.length === 2) return nums[0] * 60 + nums[1]
  if (nums.length === 3) return nums[0] * 3600 + nums[1] * 60 + nums[2]
  return null
}

/**
 * A typed number, in whatever unit the box is labelled, back to metres.
 *
 * Three decimal places on both sides of the trip, and that precision is
 * load-bearing rather than fussy: the set editor prefills from the stored value
 * and compares the resulting TEXT to decide whether anything changed. Lose a
 * digit here and opening the editor and pressing Save silently rewrites the
 * row. 40 yd -> 36.576 m -> 40 yd exactly is the case that has to hold.
 */
export function displayToMetres(
  value: number | null | undefined,
  du: DistanceUnit,
): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null
  return round(value * METRES_PER[du], 3)
}

export function metresToDisplay(
  metres: number | null | undefined,
  du: DistanceUnit,
): number | null {
  if (metres === null || metres === undefined) return null
  return round(metres / METRES_PER[du], 3)
}

function round(n: number, places: number): number {
  const f = 10 ** places
  return Math.round(n * f) / f
}

/** Epley, matching the server exactly. Used only for optimistic display of a
 *  set that hasn't synced yet — the server remains the sole author of the
 *  stored value, so the two can never disagree in the database. */
export function estimate1RM(
  kg: number | null | undefined,
  reps: number | null | undefined,
): number | null {
  if (!kg || !reps || reps < 1 || reps > 12 || kg <= 0) return null
  return round(kg * (1 + reps / 30), 2)
}

// --- plate calculator -------------------------------------------------------

/** Plates available per side, heaviest first. Kilograms. */
export const KG_PLATES = [25, 20, 15, 10, 5, 2.5, 1.25]
/** Pounds, converted to kg, so the maths stays in one unit. */
export const LB_PLATES = [45, 35, 25, 10, 5, 2.5].map((lb) => lb / LB_PER_KG)

/** Standard bars, in kg. 20 kg and 45 lb are different bars, not the same one
 *  rounded — 44.09 lb of bar is not a thing that exists in a pounds gym. */
export const KG_BAR = 20
export const LB_BAR = 45 / LB_PER_KG

/**
 * The bar and plates a person in this unit actually has in front of them.
 *
 * `LB_PLATES` was defined correctly on the first day and read by nothing, so
 * every plate breakdown in the app was computed from a 20 kg bar and metric
 * plates and then converted for display. A 135 lb bench — three 45s, the most
 * common weight in any gym — came out as "Per side: 44.1 (approx)".
 */
export function gymFor(unit: Unit): { barKg: number; plates: number[] } {
  return unit === 'lb'
    ? { barKg: LB_BAR, plates: LB_PLATES }
    : { barKg: KG_BAR, plates: KG_PLATES }
}

export type PlateResult = {
  perSide: number[]
  /** Weight that couldn't be made with the available plates. */
  remainderKg: number
}

/**
 * Which plates to load per side for a target weight.
 *
 * Greedy is correct here rather than merely convenient: plate denominations are
 * chosen so each is at least the sum of a reasonable count of the next one
 * down, so taking the heaviest that fits never strands you.
 */
export function platesFor(
  targetKg: number,
  barKg = 20,
  plates: number[] = KG_PLATES,
): PlateResult {
  const perSideTotal = (targetKg - barKg) / 2
  if (perSideTotal <= 0) return { perSide: [], remainderKg: Math.max(0, targetKg - barKg) }

  const out: number[] = []
  let left = perSideTotal
  for (const p of plates) {
    while (left >= p - 1e-6) {
      out.push(p)
      left -= p
    }
  }
  return { perSide: out, remainderKg: round(left * 2, 2) }
}

/**
 * Snap an automatically-chosen weight to a number a gym can actually make, in
 * the unit the person reads.
 *
 * The unit matters, and rounding in the wrong one is the whole bug. Storage is
 * kilograms, so a suggestion is computed and rounded there — and 100 kg, a
 * perfectly clean number to load in kg, displays as 220.5 lb. A pound user got
 * half-pound targets they cannot put on a bar, off a plate stack that goes 45,
 * 35, 25, 10, 5. Rounding has to happen in the unit being shown.
 *
 *   lb — multiples of 5, whole numbers. That is what the plates make: 2.5s exist
 *        but come in pairs, so the bar moves in 5s.
 *   kg — the exercise's own increment, or 2.5 as the default: 1.25 kg plates in
 *        pairs, which is how a kg gym steps.
 *
 * Only for weights the app chose. A weight somebody typed is theirs and is
 * carried forward exactly, however odd — see prefillFrom.
 */
export function roundToGymStep(
  kg: number | null | undefined,
  unit: Unit,
  incrementKg?: number | null,
): number {
  if (kg === null || kg === undefined || Number.isNaN(kg) || kg <= 0) return 0

  if (unit === 'lb') {
    const lb = kg * LB_PER_KG
    // Never round down to nothing: the lightest thing on the floor is a 5.
    const snapped = Math.max(5, Math.round(lb / 5) * 5)
    return round(snapped / LB_PER_KG, 3)
  }

  const step = incrementKg && incrementKg > 0 ? incrementKg : 2.5
  return round(Math.max(step, Math.round(kg / step) * step), 2)
}

/**
 * Round a target to something the plates can actually make.
 *
 * Barbells only, and only in the unit whose `plates` you pass. For a cable
 * stack or a selectorised machine the pin positions are the step, which is what
 * `increment_kg` records and what `roundToGymStep` uses — snapping those to
 * barbell plate pairs produced weights no machine can be set to.
 */
export function roundToLoadable(
  targetKg: number,
  barKg = 20,
  plates: number[] = KG_PLATES,
): number {
  const smallest = plates[plates.length - 1]
  const step = smallest * 2
  const above = Math.max(barKg, Math.round((targetKg - barKg) / step) * step + barKg)
  return round(above, 2)
}

/**
 * The digits typed so far, laid out as a clock, filling from the right.
 *
 * A phone's numeric keypad has no colon key, so "1:30" was unreachable on the
 * device the app is actually used on — the box took text only so that a colon
 * COULD be typed, which on Android meant it could not be. Typing the digits and
 * having the colon appear is how every stopwatch and microwave works: 1-3-0 is
 * 1:30, and the keyboard never has to change.
 *
 * One and two digits stay bare, because a bare number is seconds and "45" for a
 * plank must keep meaning 45 seconds rather than becoming 0:45 under the user's
 * fingers. From three digits up the minutes are what was meant.
 */
export function maskDuration(text: string): string {
  const d = text.replace(/\D/g, '').slice(0, 6)
  if (d.length <= 2) return d
  if (d.length <= 4) return `${d.slice(0, -2)}:${d.slice(-2)}`
  return `${d.slice(0, -4)}:${d.slice(-4, -2)}:${d.slice(-2)}`
}

/**
 * Seconds as text a duration box can hold — the inverse of `parseDuration`.
 *
 * NOT `formatDuration`, which is for reading: it writes "45s", and the trailing
 * letter makes `parseDuration` return null. Prefilling a carried 45-second
 * plank with it put an unparseable value in the box, which read as an empty
 * measure and disabled the button that would have logged the set.
 */
export function durationToInput(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return ''
  const total = Math.max(0, Math.round(seconds))
  if (total === 0) return ''
  if (total < 60) return String(total)

  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}

/** Tidy a finished entry: "1:90" -> "2:30", "0:45" -> "45". Left alone if it
 *  doesn't parse, so an unfinished entry isn't rewritten out from under you. */
export function normalizeDuration(text: string): string {
  const seconds = parseDuration(text)
  return seconds === null ? text : durationToInput(seconds)
}
